import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, EmptyState, LoadingSkeleton } from "@/components/shared/States";
import { Avatar, ScorePill, IntentBadge } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Search, Trash2 } from "lucide-react";
import { timeAgo } from "@/lib/format";
import { toast } from "sonner";

export default function Contacts() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(params.get("new") === "1");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", title: "", company_name: "", phone: "", source: "Website" });

  const load = async (q = "") => {
    setItems(null);
    const { data } = await api.get(`/contacts?limit=100${q ? `&search=${encodeURIComponent(q)}` : ""}`);
    setItems(data.items); setTotal(data.total);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search]);

  const create = async () => {
    if (!form.first_name) return toast.error("First name required");
    try { await api.post("/contacts", form); toast.success("Contact created"); setOpen(false); setForm({ first_name: "", last_name: "", email: "", title: "", company_name: "", phone: "", source: "Website" }); load(); }
    catch { toast.error("Could not create contact"); }
  };
  const remove = async (e, id) => { e.stopPropagation(); await api.delete(`/contacts/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <PageHeader title="Contacts" subtitle={`${total} contacts in your CRM`} icon={Users}
        actions={<Button data-testid="new-contact-button" className="aurelo-gradient text-white" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" />New Contact</Button>} />

      <div className="mb-4 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input data-testid="contacts-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search contacts…" className="pl-9" />
      </div>

      {items === null ? <LoadingSkeleton /> : items.length === 0 ? (
        <EmptyState icon={Users} title="No contacts yet" description="Add your first contact or import from a CSV to start building relationships." actionLabel="New Contact" action={() => setOpen(true)} />
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70"><tr className="text-left text-xs font-semibold text-slate-600">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3 hidden md:table-cell">Title</th><th className="px-4 py-3 hidden lg:table-cell">Company</th>
                <th className="px-4 py-3">Score</th><th className="px-4 py-3 hidden sm:table-cell">Intent</th><th className="px-4 py-3 hidden xl:table-cell">Owner</th><th className="px-4 py-3 hidden xl:table-cell">Last</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} data-testid="contact-row" onClick={() => navigate(`/app/contacts/${c.id}`)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer group">
                    <td className="px-4 py-2.5"><div className="flex items-center gap-2.5"><Avatar name={`${c.first_name} ${c.last_name}`} src={c.avatar} size={32} /><div><div className="font-medium text-slate-900">{c.first_name} {c.last_name}</div><div className="text-xs text-slate-400">{c.email}</div></div></div></td>
                    <td className="px-4 py-2.5 hidden md:table-cell text-slate-600">{c.title}</td>
                    <td className="px-4 py-2.5 hidden lg:table-cell text-slate-600">{c.company_name}</td>
                    <td className="px-4 py-2.5"><ScorePill score={c.lead_score} /></td>
                    <td className="px-4 py-2.5 hidden sm:table-cell"><IntentBadge value={c.buying_intent} /></td>
                    <td className="px-4 py-2.5 hidden xl:table-cell text-slate-500 text-xs">{c.owner_name}</td>
                    <td className="px-4 py-2.5 hidden xl:table-cell text-slate-400 text-xs">{timeAgo(c.last_interaction)}</td>
                    <td className="px-4 py-2.5"><button onClick={(e) => remove(e, c.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>New Contact</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First name</Label><Input data-testid="contact-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" /></div>
            <div><Label>Company</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="contact-save" className="aurelo-gradient text-white" onClick={create}>Create Contact</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
